import { z } from "zod";
import { accessExecutionGrantV1Schema } from "./access-control.js";
import { intentionalMemoryTraceRefSchema, memoryTraceScopeSchema, } from "./memory-trace.js";
import { memoryEntrySchema, memoryKindSchema, memoryScopeTypeSchema } from "./memories.js";
import { modelCallUsageEvidenceSchema } from "./model-call-usage.js";
// Maximum durable memories a single curation may persist. Enforced
// server-side in the control-plane write-back handler (trust boundary),
// duplicated here so the runtime curator can self-limit before dispatch.
export const MAX_CURATED_MEMORIES = 5;
// Existing memory rows shipped to the curator so it can dedup in-prompt.
// A trimmed projection of a stored memory: enough context to avoid
// re-emitting something already curated, without the full audit envelope.
export const curationExistingMemorySchema = z.object({
    scopeType: memoryScopeTypeSchema,
    scopeId: z.string().trim().min(1),
    memoryKey: z.string().trim().min(1).max(200),
    kind: memoryKindSchema,
    summary: z.string().trim().min(1).max(500),
});
// Control-plane -> runtime dispatch body. The runtime curator entity turns
// this into a single conservative model call and writes results back via the
// worker route below.
export const memoryCurationRequestSchema = z.object({
    jobId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    runId: z.string().trim().min(1),
    sessionId: z.string().trim().min(1).optional(),
    // Present only when the control plane has a verified signed subject for the
    // run. Display/caller-attributed user ids are never memory scope authority.
    userId: z.string().trim().min(1).optional(),
    organisationId: z.string().trim().min(1).optional(),
    channel: z
        .object({
        id: z.string().trim().min(1),
        organisationId: z.string().trim().min(1),
    })
        .optional(),
    transcript: z.string(),
    existingMemories: z.array(curationExistingMemorySchema).default([]),
    model: z.string().trim().min(1),
});
// A single curated memory candidate emitted by the runtime curator. Mirrors
// the model tool-call schema; scope/kind policy and the count cap are
// re-validated by the control-plane write-back handler.
export const curationCandidateSchema = z.object({
    scopeType: memoryScopeTypeSchema,
    memoryKey: z.string().trim().min(1).max(200),
    kind: memoryKindSchema,
    summary: z.string().trim().min(1).max(500),
    confidence: z.number().min(0).max(1).default(0.7),
    value: z.record(z.string(), z.unknown()).default({}),
});
// runtime -> control-plane write-back body. jobId closes the async job; the
// candidates are applied through upsertScopedMemory after server-side policy
// enforcement.
export const memoryCurationWriteBackSchema = z.object({
    jobId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    runId: z.string().trim().min(1),
    sessionId: z.string().trim().min(1).optional(),
    memories: z.array(curationCandidateSchema).max(MAX_CURATED_MEMORIES).default([]),
    reason: z.string().trim().max(500).default(""),
    modelCall: modelCallUsageEvidenceSchema.optional(),
});
export const memoryCurationWriteBackResponseSchema = z.object({
    status: z.enum(["succeeded", "skipped"]),
    created: z.number().int().min(0),
    memories: z.array(memoryEntrySchema),
});
// ---------------------------------------------------------------------------
// Run-anchored worker memory tools (PR-C).
//
// The task-agent harness exposes explicit memory agency (load_memory_context /
// remember) that mirrors the skill catalog's pull model. Both tools call the
// control-plane over the worker seam, passing only the run's id — the
// authoritative user/channel/org scopes are resolved server-side from the run,
// never trusted from the request body.
// ---------------------------------------------------------------------------
// Harness -> control-plane query body. `query` is a case-insensitive substring
// match against memory key/summary; scopeTypes/kinds narrow the result set.
export const workerMemoryQueryRequestSchema = z.object({
    grant: accessExecutionGrantV1Schema,
    runId: z.string().trim().min(1),
    toolCallId: z.string().trim().min(1).max(240),
    tenantSlug: z.string().trim().min(1),
    scopeTypes: z.array(memoryScopeTypeSchema).nonempty().optional(),
    kinds: z.array(memoryKindSchema).nonempty().optional(),
    query: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const workerMemoryQueryTraceReceiptSchema = z.object({
    schemaVersion: z.literal(1),
    traceId: z.string().trim().min(1).max(240),
    operationId: z.string().trim().min(1).max(240),
    toolCallId: z.string().trim().min(1).max(240),
    controlPlaneRunId: z.string().trim().min(1).max(240),
    scope: memoryTraceScopeSchema,
    memoryRefs: z.array(intentionalMemoryTraceRefSchema).max(100),
}).strict().refine((receipt) => receipt.operationId === receipt.toolCallId, {
    message: "The explicit memory operation must be correlated to its tool call",
    path: ["operationId"],
});
export const workerMemoryQueryResponseSchema = z.object({
    memories: z.array(memoryEntrySchema.safeExtend({
        canonicalLink: z.string().url().optional(),
    })),
    traceReceipt: workerMemoryQueryTraceReceiptSchema,
}).strict();
